import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase.config';
import RefreshButton from './RefreshButton';
import { useUnreadReports } from '../hooks/useUnreadReports';

const MobileBottomNav = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['Business']);
  const [showConverterDropdown, setShowConverterDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get unread reports and ownership status
  const { unreadEmployeeReports, unreadOwnerReports, isOwner, markOwnerReportsAsRead } = useUnreadReports();

  // Bottom navigation tabs - Mobile optimized
  const bottomTabs = [
    {
      id: 'home',
      label: 'Home',
      icon: '🏠',
      route: '/dashboard',
      activeRoutes: ['/dashboard']
    },
    {
      id: 'parties',
      label: 'Parties',
      icon: '👥',
      route: '/parties',
      activeRoutes: ['/parties']
    },
    {
      id: 'sales',
      label: 'Sales',
      icon: '+',
      route: '/sales',
      activeRoutes: ['/sales'],
      isPrimary: true
    },
    {
      id: 'payments',
      label: 'Payment',
      icon: '💳',
      route: '/payments',
      activeRoutes: ['/payments']
    },
    {
      id: 'reports',
      label: 'Report',
      icon: '📊',
      route: '/reports',
      activeRoutes: ['/reports', '/taxes', '/business-analytics']
    }
  ];

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
      closeMenu();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Close menu on escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Listen for navbar hamburger trigger
  React.useEffect(() => {
    const toggleHandler = () => setIsMenuOpen(prev => !prev);
    window.addEventListener('toggleMobileNav', toggleHandler);
    return () => window.removeEventListener('toggleMobileNav', toggleHandler);
  }, []);

  // Same categories as desktop navigation
  const navigationItems = [
    {
      category: 'Business',
      items: [
        { to: '/items', label: 'Items' },
        { to: '/parties', label: 'Parties' },
        { to: '/fixed-assets', label: 'Fixed Assets' },
        { to: '/investments', label: 'Investments' },
        { to: '/loans', label: 'Loans' },
        { to: '/sales', label: 'Sales' },
        { to: '/purchases', label: 'Purchases' },
        { to: '/payments', label: 'Payments' },
        { to: '/expenses', label: 'Expenses' },
        { to: '/service-operations', label: 'Service Operations' },
        { to: '/service-tags', label: 'Service Tags' },
        { to: '/manufacturing', label: 'Manufacturing' },
        { to: '/bill-templates', label: 'Bill Templates' }
      ]
    },
    {
      category: 'Reports',
      items: [
        { to: '/reports', label: 'All Reports' },
        { to: '/business-analytics', label: 'Business Analytics' },
        { to: '/taxes', label: 'Taxes' }
      ]
    },
    {
      category: 'Company',
      items: [
        { to: '/company-details', label: 'Company Details' },
        { to: '/employee-management', label: 'Employees' }
      ]
    },
    {
      category: 'System',
      items: [
        { to: '/settings', label: 'Settings' },
        { to: '/login-settings', label: 'Login & Security' },
        ...(Capacitor.isNativePlatform() ? [{ to: '/mobile-settings', label: 'Mobile Settings' }] : [])
      ]
    }
  ];

  // Special buttons that match desktop navbar
  const specialButtons = [
    // Converter (for all users)
    {
      to: '#',
      label: 'Converter',
      isConverter: true
    },

    // Report to Owner (for employees)
    ...(isOwner === false ? [{
      to: '/report-to-owner',
      label: 'Report to Owner',
      badge: unreadEmployeeReports > 0 ? (unreadEmployeeReports > 99 ? '99+' : unreadEmployeeReports) : null
    }] : []),

    // Reported Issues (for owners)
    ...(isOwner === true ? [{
      to: '/reported-issues',
      label: 'Reported Issues',
      badge: unreadOwnerReports > 0 ? (unreadOwnerReports > 99 ? '99+' : unreadOwnerReports) : null
    }] : [])
  ];

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setShowConverterDropdown(false);
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
  };

  const handleConverterClick = (type) => {
    // Trigger the converter from the parent App component
    window.dispatchEvent(new CustomEvent('openConverter', { detail: { type } }));
    closeMenu();
  };

  return (
    <>
      {/* Mobile Bottom Navigation - Fixed at bottom edge (edge-to-edge). max-w-full prevents focus outline or content from overflowing. */}
      <div 
        className="md:hidden fixed left-0 right-0 max-w-[100vw] w-full bg-white border-t border-gray-200 shadow-lg z-40 overflow-x-hidden pb-[var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))]"
        style={{
          bottom: 0,
        }}
      >
        <div className="flex items-center w-full max-w-full min-w-0 px-1 py-0.5">
          {bottomTabs.map((tab) => {
            const isActive = tab.activeRoutes.includes(location.pathname);
            // Show notification for reports tab if there are unread reports
            const hasNotification = tab.id === 'reports' && (unreadEmployeeReports > 0 || unreadOwnerReports > 0);

            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.route)}
                className={`relative flex flex-col items-center justify-center py-1 px-0.5 rounded-lg transition-all duration-200 min-w-0 flex-1 basis-0 overflow-hidden touch-manipulation active:scale-95 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset ${
                  isActive
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50 active:bg-gray-100'
                }`}
              >
                <div className="relative flex-shrink-0">
                  {tab.isPrimary ? (
                    <div className="w-9 h-9 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                      <span className="text-white text-xl font-bold">{tab.icon}</span>
                    </div>
                  ) : (
                    <span className="text-xl block">{tab.icon}</span>
                  )}
                  {hasNotification && (
                    <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse border border-white"></div>
                  )}
                  {isActive && (
                    <div className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-blue-500 rounded-full"></div>
                  )}
                </div>
                <span className={`text-[10px] sm:text-[11px] font-medium transition-all duration-200 mt-0.5 truncate max-w-full ${
                  isActive ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Full Height Sidebar - Left Side (Image 2 Style) */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50" onClick={closeMenu}>
          <div 
            className="absolute top-0 left-0 h-full w-[min(20rem,calc(100vw-0.75rem))] max-w-full bg-white shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))' }}
          >
            {/* Header with Logo and Close Button */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-orange-400 rounded-lg flex items-center justify-center mr-2">
                    <span className="text-white font-bold text-sm">A₹T</span>
                  </div>
                  <span className="text-lg font-semibold">ACCTOO</span>
                </div>
                <button
                  onClick={closeMenu}
                  className="w-8 h-8 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-colors duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Navigation Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              {/* Quick access */}
              <div className="py-2 border-b border-gray-200 bg-blue-50/50">
                <Link
                  to="/dashboard"
                  onClick={closeMenu}
                  className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition-all duration-200 touch-manipulation ${
                    location.pathname === '/dashboard' ? 'bg-blue-100 text-blue-600 border-r-2 border-blue-600' : ''
                  }`}
                >
                  <span className="text-lg">🏠</span>
                  <span className="font-medium">Dashboard</span>
                </Link>
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="text-xs text-gray-500">Pull down to refresh</span>
                  <RefreshButton size="sm" />
                </div>
              </div>

              {/* Special Buttons - Same as Desktop Navbar */}
              {specialButtons.length > 0 && (
                <div className="py-2 border-b border-gray-200">
                  {specialButtons.map((button, index) => {
                    const isActive = location.pathname === button.to;
                    
                    if (button.isConverter) {
                      return (
                        <div key={index} className="relative">
                          <button
                            onClick={() => setShowConverterDropdown(!showConverterDropdown)}
                            className="flex items-center justify-between w-full px-4 py-3 text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition-all duration-200"
                          >
                            <div className="flex items-center">
                              <span className="font-medium">{button.label}</span>
                            </div>
                            <svg 
                              className={`w-4 h-4 transition-transform duration-200 ${
                                showConverterDropdown ? 'rotate-180' : ''
                              }`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {showConverterDropdown && (
                            <div className="bg-gray-50 border-l-4 border-blue-500">
                              <button
                                onClick={() => handleConverterClick('currency')}
                                className="flex items-center w-full px-6 py-3 text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition-all duration-200"
                              >
                                💱 Currency
                              </button>
                              <button
                                onClick={() => handleConverterClick('unit')}
                                className="flex items-center w-full px-6 py-3 text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition-all duration-200"
                              >
                                📏 Unit
                              </button>
                              <button
                                onClick={() => handleConverterClick('gst')}
                                className="flex items-center w-full px-6 py-3 text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition-all duration-200"
                              >
                                💰 GST Calculator
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    return (
                      <Link
                        key={index}
                        to={button.to}
                        onClick={() => {
                          closeMenu();
                          // Mark owner reports as read when clicking on Reported Issues
                          if (button.to === '/reported-issues' && markOwnerReportsAsRead) {
                            markOwnerReportsAsRead();
                          }
                        }}
                        className={`flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition-all duration-200 ${
                          isActive ? 'bg-blue-100 text-blue-600 border-r-2 border-blue-600' : ''
                        }`}
                      >
                        <div className="flex items-center">
                          <span className="font-medium">{button.label}</span>
                          {isActive && (
                            <div className="ml-2 w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                          )}
                        </div>
                        {button.badge && (
                          <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {button.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}

              {/* Main Categories with Clubbing - Same as Desktop */}
              <div className="py-4">
                {navigationItems.map((category, categoryIndex) => {
                  const isExpanded = expandedCategories.includes(category.category);
                  return (
                    <div key={categoryIndex} className="border-b border-gray-100 last:border-b-0">
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(category.category)}
                        className={`w-full px-4 py-3 text-left transition-colors duration-200 ${
                          isExpanded ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{category.category}</span>
                          <svg 
                            className={`w-4 h-4 transition-transform duration-200 ${
                              isExpanded ? 'rotate-180' : ''
                            }`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                      
                      {/* Category Items */}
                      {isExpanded && (
                        <div className="bg-gray-50">
                          {category.items.map((item, itemIndex) => {
                            const isActive = location.pathname === item.to;
                            return (
                              <Link
                                key={itemIndex}
                                to={item.to}
                                onClick={closeMenu}
                                className={`flex items-center px-6 py-3 text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition-all duration-200 ${
                                  isActive ? 'bg-blue-100 text-blue-600 border-r-2 border-blue-600' : ''
                                }`}
                              >
                                <span className="font-medium">{item.label}</span>
                                {isActive && (
                                  <div className="ml-auto w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Logout Section */}
              <div className="py-2 border-t border-gray-100">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all duration-200"
                >
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-4 border-t border-gray-100 flex-shrink-0">
              <div className="text-center text-sm text-gray-500">
                <p>ACCTOO - Simplifying Accounting, Together</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileBottomNav; 